import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import fullLogo from '../SE_Logo_tagline.svg';
import appConfig from '../config';
import './ForgotPassword.css';

const toastConfig = { autoClose: 4000, position: 'top-right' };
const successPrefix = '\u2705';
const errorPrefix = '\u274c';

function normaliseEmail(email) {
  return String(email || '').trim().toLowerCase();
}

function getFriendlyAmplifyError(error) {
  if (!error) {
    return 'Something went wrong. Please try again.';
  }

  if (error.code === 'UserNotFoundException') {
    return 'No account was found for that email address.';
  }

  if (error.code === 'LimitExceededException') {
    return 'Too many attempts. Please wait a few minutes before trying again.';
  }

  if (error.code === 'CodeMismatchException') {
    return 'The reset code is incorrect.';
  }

  if (error.code === 'ExpiredCodeException') {
    return 'The reset code has expired. Please request a new code.';
  }

  if (error.code === 'InvalidPasswordException') {
    return 'The new password does not meet the password requirements.';
  }

  return error.message || 'Something went wrong. Please try again.';
}

function ForgotPasswordLayout({ children }) {
  return (
    <main className="forgotPasswordPage">
      <ToastContainer />
      <section className="forgotPasswordPanel" aria-label="Password reset">
        <Link className="forgotPasswordLogoLink" to="/" aria-label="Go to homepage">
          <img className="forgotPasswordLogo" src={fullLogo} alt="School Engine" />
        </Link>
        {children}
        <footer className="forgotPasswordFooter">
          <p>App Version: {appConfig.APP_VERSION_NUMBER}</p>
          <p>&copy; School Engine 2026</p>
        </footer>
      </section>
    </main>
  );
}

export function ForgotPasswordRequest() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();

    const cleanedEmail = normaliseEmail(email);
    if (!cleanedEmail) {
      toast.error(`${errorPrefix} Please enter your email address.`, toastConfig);
      return;
    }

    setIsSubmitting(true);
    try {
      await Auth.forgotPassword(cleanedEmail);
      navigate(
        `/forgot-password/confirm?email=${encodeURIComponent(cleanedEmail)}`,
        { state: { resetCodeSent: true } }
      );
    } catch (error) {
      toast.error(`${errorPrefix} ${getFriendlyAmplifyError(error)}`, toastConfig);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <ForgotPasswordLayout>
      <h1 className="forgotPasswordTitle">Reset password</h1>
      <p className="forgotPasswordText">Enter your School Engine email address and we will send you a reset code.</p>
      <form className="forgotPasswordForm" onSubmit={handleSubmit}>
        <label className="forgotPasswordLabel" htmlFor="forgot-password-email">Email address</label>
        <input
          className="forgotPasswordInput"
          id="forgot-password-email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
          disabled={isSubmitting}
        />
        <button className="forgotPasswordButton" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Sending code...' : 'Send reset code'}
        </button>
      </form>
      <Link className="forgotPasswordSecondaryLink" to="/app">Back to login</Link>
    </ForgotPasswordLayout>
  );
}

export function ForgotPasswordConfirm() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();

    const cleanedEmail = normaliseEmail(email);
    const cleanedCode = String(code || '').trim();

    if (!cleanedEmail || !cleanedCode || !newPassword || !confirmPassword) {
      toast.error(`${errorPrefix} Please complete all fields.`, toastConfig);
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(`${errorPrefix} New password and confirmation do not match.`, toastConfig);
      return;
    }

    setIsSubmitting(true);
    try {
      await Auth.forgotPasswordSubmit(cleanedEmail, cleanedCode, newPassword);
      toast.success(`${successPrefix} Password reset. Please sign in with your new password.`, toastConfig);
      window.setTimeout(() => navigate('/app'), 1200);
    } catch (error) {
      toast.error(`${errorPrefix} ${getFriendlyAmplifyError(error)}`, toastConfig);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <ForgotPasswordLayout>
      <h1 className="forgotPasswordTitle">Enter reset code</h1>
      {location.state?.resetCodeSent && (
        <p className="forgotPasswordSuccessText">Reset code sent. Please check your email.</p>
      )}
      <p className="forgotPasswordText">Use the code from your email and choose a new password.</p>
      <form className="forgotPasswordForm" onSubmit={handleSubmit}>
        <label className="forgotPasswordLabel" htmlFor="forgot-password-confirm-email">Email address</label>
        <input
          className="forgotPasswordInput"
          id="forgot-password-confirm-email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
          disabled={isSubmitting}
        />
        <label className="forgotPasswordLabel" htmlFor="forgot-password-code">Reset code</label>
        <input
          className="forgotPasswordInput"
          id="forgot-password-code"
          type="text"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          autoComplete="one-time-code"
          disabled={isSubmitting}
        />
        <label className="forgotPasswordLabel" htmlFor="forgot-password-new">New password</label>
        <input
          className="forgotPasswordInput"
          id="forgot-password-new"
          type="password"
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          autoComplete="new-password"
          disabled={isSubmitting}
        />
        <label className="forgotPasswordLabel" htmlFor="forgot-password-confirm">Confirm new password</label>
        <input
          className="forgotPasswordInput"
          id="forgot-password-confirm"
          type="password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          autoComplete="new-password"
          disabled={isSubmitting}
        />
        <button className="forgotPasswordButton" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Resetting password...' : 'Reset password'}
        </button>
      </form>
      <div className="forgotPasswordLinkRow">
        <Link className="forgotPasswordSecondaryLink" to="/forgot-password">Request a new code</Link>
        <Link className="forgotPasswordSecondaryLink" to="/app">Back to login</Link>
      </div>
    </ForgotPasswordLayout>
  );
}
